// KaziPeople Web (Work Browser) — Phase 1. A thin, domain-neutral surface that
// launches, honestly represents, and connects external resources to KaziPeople
// work context. No arbitrary embedding (Phase 2), no browsing-history surveillance.
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Globe, Search, Clock, X, ArrowLeft, Settings, Shield } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useHasAnyPermission } from '@/rbac/useRbac';
import { listBookmarks, getPolicy } from '@/lib/webApi';
import { classifyUrl, coerceUrl, faviconFor, openExternal, CAPABILITY_IS_OPENABLE } from './lib/urlPolicy';
import { loadRecents, pushRecent, clearRecents } from './lib/recents';
import { WebResourceView } from './components/WebResourceView';
import { WebContextPanel } from './components/WebContextPanel';
import type { Bookmark, RecentResource, UrlClassification, WorkContext } from './lib/types';

// Read an optional Work Context from navigation state or query params (so Work,
// Conversations, Payroll, etc. can launch the browser with context + a return route).
function useLaunchContext(): { context: WorkContext | null; initialUrl: string | null } {
  const location = useLocation();
  const [params] = useSearchParams();
  return useMemo(() => {
    const stateCtx = (location.state as any)?.workContext as WorkContext | undefined;
    const qType = params.get('ctxType');
    const context: WorkContext | null =
      stateCtx ??
      (qType
        ? {
            type: qType as WorkContext['type'],
            id: params.get('ctxId') ?? '',
            title: params.get('ctxTitle') ?? undefined,
            sourceRoute: params.get('return') ?? undefined,
          }
        : null);
    return { context, initialUrl: params.get('url') };
  }, [location.state, params]);
}

export const WebPage: React.FC = () => {
  const { t } = useTranslation('webBrowser');
  const navigate = useNavigate();
  const loc = useLocation();
  // Company admins/owners (tenant:update:own) or a role granted the precise browser
  // manage permission (built-in HR or a custom role) can reach the bookmarks/policy admin.
  const canManage = useHasAnyPermission(['browser:bookmarks.manage', 'browser:policy.manage', 'tenant:update:own']);
  const { context, initialUrl } = useLaunchContext();

  const [input, setInput] = useState('');
  const [resource, setResource] = useState<UrlClassification | null>(null);
  const [title, setTitle] = useState('');
  const [recents, setRecents] = useState<RecentResource[]>(() => loadRecents());

  const { data: bookmarks = [] } = useQuery({ queryKey: ['web', 'bookmarks'], queryFn: listBookmarks });
  const { data: policy = null } = useQuery({ queryKey: ['web', 'policy'], queryFn: getPolicy });

  const open = (url: string, displayTitle?: string) => {
    const cls = classifyUrl(url, policy);
    setResource(cls);
    setTitle(displayTitle ?? cls.domain ?? url);
    setInput(cls.url);
    if (CAPABILITY_IS_OPENABLE(cls.capability) && cls.domain) {
      setRecents(pushRecent({ url: cls.url, title: displayTitle ?? cls.domain, domain: cls.domain, lastAccess: Date.now() }));
    }
  };

  // Auto-open a URL passed at launch (e.g. from a Work assignment resource).
  useEffect(() => {
    if (initialUrl) open(initialUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = coerceUrl(input);
    if (!url) {
      toast(t('search_hint', { defaultValue: 'Enter a web address (e.g. nmbbank.co.tz). Search is coming soon.' }));
      return;
    }
    open(url);
  };

  const sendToConversation = () => {
    if (!resource) return;
    navigator.clipboard?.writeText(resource.url).catch(() => {});
    toast.success(t('sent_to_conv', { defaultValue: 'Link copied — paste it into a conversation.' }));
    navigate(loc.pathname.replace(/\/web$/, '/messages'));
  };

  return (
    <div className="space-y-5">
      {/* Branded header — the shell already carries KaziPeople branding. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-500/20">
            <Globe className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('title', { defaultValue: 'KaziPeople Web' })}</h1>
            <p className="text-sm text-muted-foreground">{t('tagline', { defaultValue: 'The web, connected to your work.' })}</p>
          </div>
        </div>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => navigate(loc.pathname.replace(/\/web$/, '/settings/web'))}>
            <Settings className="mr-1.5 h-4 w-4" /> {t('manage', { defaultValue: 'Manage' })}
          </Button>
        )}
      </div>

      {/* Address bar */}
      <form onSubmit={submit} className="flex items-center gap-2">
        {resource && (
          <Button type="button" variant="outline" size="icon" aria-label={t('back', { defaultValue: 'Back to start' })} onClick={() => { setResource(null); setInput(''); }}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('address_placeholder', { defaultValue: 'Search or enter a web address' })}
            inputMode="url"
            className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm text-foreground outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-500/20"
          />
        </div>
        <Button type="submit">{t('go', { defaultValue: 'Go' })}</Button>
      </form>

      <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
        <div>
          {resource ? (
            <WebResourceView resource={resource} title={title} workContext={context} onSendToConversation={sendToConversation} />
          ) : (
            <WebHome bookmarks={bookmarks} recents={recents} onOpen={open} onClearRecents={() => { clearRecents(); setRecents([]); }} t={t} />
          )}
        </div>
        {context && (
          <div className="lg:pt-0">
            <WebContextPanel context={context} />
          </div>
        )}
      </div>

      {/* Transparency: the feature promises no browsing surveillance — link to the details. */}
      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={() => navigate(loc.pathname.replace(/\/web$/, '/web/privacy'))}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-foreground"
        >
          <Shield className="h-3.5 w-3.5" /> {t('privacy_link', { defaultValue: 'Privacy & security' })}
        </button>
      </div>
    </div>
  );
};

// ── Home: company bookmarks (by category) + recent work resources ──────────────
const WebHome: React.FC<{
  bookmarks: Bookmark[];
  recents: RecentResource[];
  onOpen: (url: string, title?: string) => void;
  onClearRecents: () => void;
  t: (k: string, o?: any) => string;
}> = ({ bookmarks, recents, onOpen, onClearRecents, t }) => {
  const byCategory = useMemo(() => {
    const map = new Map<string, Bookmark[]>();
    for (const b of bookmarks) {
      const arr = map.get(b.category) ?? [];
      arr.push(b);
      map.set(b.category, arr);
    }
    return [...map.entries()];
  }, [bookmarks]);

  return (
    <div className="space-y-6">
      {byCategory.length === 0 && recents.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <Globe className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">{t('empty_title', { defaultValue: 'No company bookmarks yet' })}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('empty_body', { defaultValue: 'Enter a web address above, or ask your admin to add company resources.' })}</p>
        </div>
      )}

      {byCategory.map(([category, items]) => (
        <section key={category}>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{category}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {items.map((b) => {
              const domain = safeDomain(b.url);
              const fav = faviconFor(domain);
              return (
                <button
                  key={b.id}
                  onClick={() => onOpen(b.url, b.name)}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:border-primary-300 hover:shadow-sm"
                >
                  {b.icon && !/^https?:/i.test(b.icon) ? (
                    <span className="text-lg">{b.icon}</span>
                  ) : fav ? (
                    <img src={fav} alt="" className="h-6 w-6 rounded" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  ) : (
                    <Globe className="h-6 w-6 text-muted-foreground" />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">{b.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{domain}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {recents.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {t('recent', { defaultValue: 'Recent work resources' })}
            </h2>
            <button onClick={onClearRecents} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" /> {t('clear', { defaultValue: 'Clear' })}
            </button>
          </div>
          <div className="divide-y divide-border rounded-xl border border-border bg-card">
            {recents.map((r) => (
              <button key={r.url} onClick={() => onOpen(r.url, r.title)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50">
                <img src={faviconFor(r.domain) ?? ''} alt="" className="h-5 w-5 rounded" onError={(e) => (e.currentTarget.style.display = 'none')} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground">{r.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{r.domain}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

function safeDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export default WebPage;
